import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import * as GripCDP from 'grip-cdp-tracker';
import moment from 'moment-timezone';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ConfigService } from 'app/config.service';
import { ApiSearchFacet } from 'app/shared/_components/filtered-lists/_shared/_models/search.model';
import { ContactStatus } from '../_enums/contact-status.enum';
import { ApiResponseModel } from '../_models/api-response.model';
import { ChatTokenModel } from '../_models/chat-token.model';
import { HostedBuyerContainerModeModel } from '../_models/hosted-buyer-container-mode.model';
import { SessionCheckinExportModel } from '../_models/session-checkin-export.model';
import { SubmitUserScheduleRequestModel } from '../_models/submit-user-schedule-request.model';
import { TeamSettings } from '../_models/team-settings.model';
import { UtmSource } from '../_models/utm-source.model';
import { buildUrlParams } from '../utils';
import { MeetingSlotLocation } from '../_models/meeting-slot.model';
import { MeetingSwapEntry } from '../_models/meeting-swap.model';
import { ScheduleData } from '../_models/schedule.model';
import { TlrConfig } from 'app/private/teams/team-plans/team-plans.model';

@Injectable({
	providedIn: 'root'
})
export class ApiService {
	API_URL: string;
	API_URL_SHORT: string;
	TEAMS_API_URL: string;

	constructor(
		private readonly http: HttpClient,
		private readonly configService: ConfigService,
	) {
		this.API_URL = environment.apiUrl;
		this.API_URL_SHORT = environment.apiUrlShort;
		this.TEAMS_API_URL = environment.teamsApiUrl;
	}

	/**
	 * Logged in
	 */

	getContainerData(purgeCache?: boolean): Observable<any> {
		let request_url = this.API_URL + 'container/' + this.configService.containerId;
		// Purge cache to ensure users active=1 flag is set & most up-to-date extensions received
		if (purgeCache) request_url += '?no_cache=' + Math.random();
		return this.http
			.get(request_url)
			.pipe(map((res: any) => res.data));
	}

	// Gets all extensions for the container for all platforms by removing the X-Grip-Version header
	getAllContainerExtensions(): Observable<any> {
		const headers = new HttpHeaders({
			'X-Skip-Version': 'true'
		});

		let request_url = this.API_URL + 'container/' + this.configService.containerId + '/extensions';
		return this.http
			.get(request_url, { headers })
			.pipe(map((res: any) => res.data));
	}

	getContainerThings(search?: string): Observable<any> {
		let request_url = this.API_URL + `container/${this.configService.containerId}/thing?show_inactive=true`;
		request_url += search ? `&search=${search}` : '';
		return this.http
			.get(request_url)
			.pipe(map((res: any) => res.data));
	}

	getThingData(id: number, purgeCache?: boolean): Observable<any> {
		let request_url = this.API_URL + `container/${this.configService.containerId}/thing/${id}`;
		// When loading own profile, no_cache is used to prevent caching the responses
		// This ensures the user always gets the most up-to-date data (e.g. after linkedin profile info import)
		if (purgeCache) request_url += '?no_cache=' + Math.random();

		return this.http
			.get(request_url)
			.pipe(map((res: any) => res.data));
	}

	getThingContactDetails(id: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/' +
				id +
				'/contact_details'
			)
			.pipe(map((res: any) => res.data));
	}

	getFindSearch(params, page): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/registered/detail?search=' +
				params +
				'&page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getUpdateLinkedInMe(): Observable<any> {
		return this.http
			.get(this.API_URL + 'me/update')
			.pipe(map((res: any) => res.data));
	}

	postContainerRelationship(
		relationship_id: number,
		to_thing_id: number,
		email: string
	): Observable<any> {
		const object = {
			relationship_id,
			thing_email: email,
			to_thing_id
		};

		return this.http
			.post(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/relationship',
				object
			)
			.pipe(map((res: any) => res.data));
	}

	/**
	 * Lists
	 */
	getInterestListSkipped(page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/skipped?page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListHostedSkipped(page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/phase/pre-live/skipped?page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListInterested(page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/interested?page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListHostedInterested(page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/phase/pre-live/interested?page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListSuperInterested(page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/super_interested?page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListInterestedInYou(page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/interested_in_you' +
				(page === -1 ? '' : '?page=' + page)
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListSkippedSearch(search: string, page: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/skipped?search=' +
				search +
				'&page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListInterestedSearch(
		search: string,
		page: number
	): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/interested?search=' +
				search +
				'&page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getInterestListInterestedInYouSearch(
		search: string,
		page: number
	): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/interested_in_you?search=' +
				search +
				'&page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getPromotedThings(param: string): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/promoted/detail' +
				param
			)
			.pipe(map((res: any) => res.data));
	}

	/**
	 * Profile
	 */
	patchThingProfile(body: any): Observable<any> {
		return this.http
			.patch(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id/profile',
				body
			)
			.pipe(map((res: any) => res.data));
	}

	postMeGeneric(data): Observable<any> {
		return this.http
			.patch(this.API_URL + 'thing/user_id', data)
			.pipe(
				map(
					(res: any) => res.data
				),
				catchError(
					(error: HttpErrorResponse) => {
						console.log('error', error);
						return throwError(error);
					}
				)
			);
	}

	postUpdateImage(
		image: any,
		heroImage: any,
		thingId: number
	): Observable<any> {
		const file: File = image;
		const heroFile: File = heroImage;
		const formData: FormData = new FormData();
		if (file) formData.append('picture', file, file.name);
		if (heroFile) formData.append('hero_picture', heroFile, heroFile.name);

		return this.http
			.post(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'/image',
				formData
			)
			.pipe(map((res: any) => res.data));
	}

	postUpdatePicture(image: any, thingId: number): Observable<any> {
		const file: File = image;
		const formData: FormData = new FormData();
		if (file) formData.append('picture', file, file.name);

		return this.http
			.post(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'/picture',
				formData
			)
			.pipe(map((res: any) => res.data));
	}

	postAdditionalImages(
		imagesToUpload: object,
		thingId: number
	): Observable<any> {
		const formData: FormData = new FormData();
		if (imagesToUpload) {
			for (const [key, value] of Object.entries(imagesToUpload)) {
				if (key.indexOf('_url_') !== -1) {
					formData.append(key, value);
				} else {
					formData.append(key, value, value.name);
				}
			}
		}
		return this.http
			.post(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'/images',
				formData
			)
			.pipe(map((res: any) => res.data));
	}

	/**
	 * Meetings
	 */

	// Get all meetings from pending, declined and accepted (used for pending meetings)
	getMeetings(): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'meeting/thing/user_id?container_id=' +
				this.configService.containerId
			)
			.pipe(map((res: any) => res.data));
	}

	// Get meeting slots for given attendee ids
	getMeetingSlots(params: string): Observable<MeetingSlotLocation[]> {
		return this.http
			.get(
				this.API_URL + 'meeting/available_slots/thing' + params
			)
			.pipe(map((res: ApiResponseModel<MeetingSlotLocation[]>) => res.data));
	}

	getThingAvailabilityForMeeting(meetingId, thingId): Observable<any> {
		return this.http
			.put(
				this.API_URL +
				'meeting/' +
				meetingId +
				'/multiuser/thing/' +
				thingId +
				'/available',
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	getMeetingLocationTimezones(): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'meeting/location/container/' +
				this.configService.containerId +
				'/timezone'
			)
			.pipe(map((res: any) => res.data));
	}

	getMeetingWithUser(thingId, inFuture): Observable<any> {
		let nowUnix: any = moment().unix();
		if (!inFuture) {
			nowUnix = '';
		}
		return this.http
			.get(
				this.API_URL +
				'meeting/thing/user_id?container_id=' +
				this.configService.containerId +
				'&end_after=' +
				nowUnix +
				'&search=' +
				thingId
			)
			.pipe(map((res: any) => res.data));
	}

	getMeetingsWithThing(userId, thingId, inFuture, purgeCache: boolean = false, isInTeam: 1 | 0 = 0): Observable<any> {
		let nowUnix: any = moment().unix();
		if (!inFuture) {
			nowUnix = '';
		}

		// Only purge the cache for specific requests.
		let purgeCacheUrl = "";
		if (purgeCache === true) purgeCacheUrl = "&no_cache=" + Math.random();

		return this.http
			.get(
				this.API_URL +
				'meeting/thing/' +
				userId +
				'?container_id=' +
				this.configService.containerId +
				'&start_after=' +
				nowUnix +
				'&search=' +
				thingId +
				'&team_members_feedback=' +
				isInTeam +
				purgeCacheUrl
			)
			.pipe(map((res: any) => res.data));
	}

	/**
	 * SEARCH
	 **/
	getThingTypes(): Observable<any> {
		return this.http
			.get(this.API_URL + 'thing/type')
			.pipe(map((res: any) => res.data));
	}

	getSearch(param: string) {
		return this.http
			.get(`${this.API_URL}container/${this.configService.containerId}/search/${param}`)
			.pipe(map((res: any) => res.data));
	}


	getSearchFacetsByType$(param: string): Observable<ApiSearchFacet[]> {
		return this.http
			.get(`${this.API_URL}container/${this.configService.containerId}/search/facet/value${param}`)
			.pipe(map((res: any) => res.data));
	}

	searchFiltersOver500(param: string): Observable<any[]> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/search/filter/value' +
				param
			)
			.pipe(map((res: any) => res.data));
	}

	searchFacetsOver500(facetName: string, params: HttpParams): Observable<any[]> {
		const options = {
			params: params
		};
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/search/facet/' +
				facetName +
				'/value',
				options
			)
			.pipe(map((res: any) => res.data));
	}

	getFeaturedThings(numberToGet?: number): Observable<any> {
		const param = numberToGet ? '?number_of_suggestions=' + numberToGet : '';
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/search/featured' +
				param
			)
			.pipe(map((res: any) => res.data));
	}

	getSchedule(param: string): Observable<ScheduleData> {
		return this.http
			.get(
				this.API_URL +
				'schedule/thing/user_id/info?container_id=' +
				this.configService.containerId +
				'&' +
				param
			)
			.pipe(map((res: ApiResponseModel<ScheduleData>) => res.data));
	}

	postSkipRecommendedSession(sessionId): Observable<any> {
		return this.http
			.post(this.API_URL + 'session/' + sessionId + '/recommendation/skip', {})
			.pipe(map((res: any) => res.data));
	}

	putOnboardingStatus(body, userId): Observable<any> {
		return this.http
			.put(this.API_URL + `container/${this.configService.containerId}/thing/${userId}/onboarding`, body)
			.pipe(map((res: any) => res.data));
	}

	// NOTIFICATIONS
	getNotifications(): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'user/notification?container_id=' +
				this.configService.containerId
			)
			.pipe(map((res: any) => res.data));
	}

	getNotificationsUnread(): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'user/notification?container_id=' +
				this.configService.containerId +
				'&mark_unread=1'
			)
			.pipe(map((res: any) => res.data));
	}

	getMoreNotifications(last: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'user/notification?container_id=' +
				this.configService.containerId +
				'&last=' +
				last
			)
			.pipe(map((res: any) => res.data));
	}

	postNotificationClicked(notificationId, body): Observable<any> {
		return this.http
			.post(this.API_URL + 'user/notification/' + notificationId, body)
			.pipe(map((res: any) => res.data));
	}

	// PENDING / ORGANIZER MEETINGS LIMIT
	getPendingAndOrganizerMeetingLimit(thingId: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'/meeting/pending_limit'
			)
			.pipe(map((res: any) => res.data));
	}

	// CHAT
	getChatLatest(thingId: any): Observable<any[]> {
		// no thing id gets all connections
		return this.http
			.get(
				this.API_URL +
				'chat/latest?container_id=' +
				this.configService.containerId +
				(thingId ? '&thing_id=' + thingId : '')
			)
			.pipe(map((res: any) => res.data));
	}

	getThingChatLatest(thingId): Observable<any[]> {
		return this.http
			.get(
				this.API_URL +
				'chat/thing/' +
				thingId +
				'/latest?container_id=' +
				this.configService.containerId
			)
			.pipe(map((res: any) => res.data));
	}

	getChatMessages(thingId): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'chat/thing/' + thingId)
			.pipe(map((res: any) => res.data));
	}

	getCompanyChatMessages(companyId, thingId): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'chat/thing/' + companyId + '/to/' + thingId)
			.pipe(map((res: any) => res.data));
	}

	postChatMessage(thingId: number, body: any, actionOrigin: UtmSource): Observable<any[]> {
		if (body) body.container_id = this.configService.containerId;
		return this.http
			.post(this.API_URL + 'chat/thing/' + thingId, body)
			.pipe(
				tap(() => {
					GripCDP.trackEvent('send_chat', 'thing', thingId, { utm_source: actionOrigin || '' });
				}),
				map((res: any) => res.data)
			);
	}

	postCompanyChatMessage(companyId, thingId, body): Observable<any[]> {
		if (body) body.container_id = this.configService.containerId;
		return this.http
			.post(this.API_URL + 'chat/thing/' + companyId + '/to/' + thingId, body)
			.pipe(map((res: any) => res.data));
	}

	markChatMessageAsRead(thingId): Observable<any[]> {
		return this.http
			.patch(this.API_URL + 'chat/thing/' + thingId + '/read', {})
			.pipe(map((res: any) => res.data));
	}

	markCompanyChatMessageAsRead(companyId, thingId): Observable<any[]> {
		return this.http
			.patch(
				this.API_URL + 'chat/thing/' + companyId + '/to/' + thingId + '/read',
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	postCompanyThingOnline(companyId): Observable<ChatTokenModel> {
		return this.http
			.post(this.API_URL + 'chat/thing/' + companyId + '/online', {})
			.pipe(map((res: any) => res.data));
	}

	postChatThingOnline(): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'chat/thing/user_id/online', {})
			.pipe(map((res: any) => res.data));
	}

	postChatThingOffline(): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'chat/thing/user_id/offline', {})
			.pipe(map((res: any) => res.data));
	}

	// TEAMS
	postCreateTeam(body): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'team/create', body)
			.pipe(map((res: any) => res.data));
	}

	getUserTeamStatus(): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'team')
			.pipe(map((res: any) => res.data));
	}

	postInviteTeamMembers(teamId, members): Observable<any[]> {
		return this.http
			.post(this.TEAMS_API_URL + 'team/' + teamId + '/member', members)
			.pipe(map((res: any) => res.data));
	}

	getTeamDetail(): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/detail?container_id=' +
				this.configService.containerId
			)
			.pipe(map((res: any) => res.data));
	}

	getTeamSettings(typeId): Observable<TeamSettings> {
		return this.http
			.get(this.API_URL +
				'container/' + this.configService.containerId +
				'/type/' + typeId + '/teams_settings')
			.pipe(map((res: ApiResponseModel<TeamSettings>) => res.data));
	}

	getTeamMeetings(
		teamId,
		teamsEventId,
		queryParams: string = '',
		isExport: boolean = false,
	): Observable<ScheduleData> {
		let headers: HttpHeaders = new HttpHeaders();
		if (isExport) {
			headers = new HttpHeaders({ 'X-Grip-Export': '1' });
		}

		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				teamsEventId +
				'/schedule/info' +
				queryParams,
				{ headers }
			)
			.pipe(map((res: ApiResponseModel<ScheduleData>) => res.data));
	}

	patchTeamMember(teamId, userId, body): Observable<any[]> {
		return this.http
			.patch(this.TEAMS_API_URL + 'team/' + teamId + '/member/' + userId, body)
			.pipe(map((res: any) => res.data));
	}

	patchEventTeamMember(teamId, eventId, userId, body): Observable<any[]> {
		return this.http
			.patch(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/member/' +
				userId,
				body
			)
			.pipe(map((res: any) => res.data));
	}

	patchEventTeamMemberProfile(
		teamId,
		eventId,
		userId,
		body
	): Observable<{
		first_name: string;
		last_name: string;
		headline: string;
		job_title: string;
		location: string;
		summary: string;
	}> {
		return this.http
			.patch(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/member/' +
				userId +
				'/profile',
				body
			)
			.pipe(map((res: any) => res.data));
	}

	getEventTeamMembers(teamId, eventId): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL + 'team/' + teamId + '/event/' + eventId + '/member/'
			)
			.pipe(map((res: any) => res.data));
	}

	getEventTeamMemberProfile(teamId, eventId, userId): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/member/' +
				userId
			)
			.pipe(map((res: any) => res.data));
	}

	postTeamsEventsLogin(): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'team/login', {
				container_id: this.configService.containerId
			})
			.pipe(map((res: any) => res.data));
	}

	deleteTeamMember(teamId, userId): Observable<any[]> {
		return this.http
			.delete(this.TEAMS_API_URL + 'team/' + teamId + '/member/' + userId)
			.pipe(map((res: any) => res.data));
	}

	deletePendingMember(teamId, userId): Observable<any[]> {
		return this.http
			.delete(
				this.TEAMS_API_URL + 'team/' + teamId + '/member/' + userId + '/invite'
			)
			.pipe(map((res: any) => res.data));
	}

	getTeamEventItems(teamId, eventId): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL + 'team/' + teamId + '/event/' + eventId + '/item'
			)
			.pipe(map((res: any) => res.data));
	}

	getTeamEventItemsOne(teamId, eventId, itemId): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/item/' +
				itemId
			)
			.pipe(map((res: any) => res.data));
	}

	postTeamEventItem(teamId, eventId, body): Observable<any[]> {
		return this.http
			.post(
				this.TEAMS_API_URL + 'team/' + teamId + '/event/' + eventId + '/item',
				body
			)
			.pipe(map((res: any) => res.data));
	}

	patchTeamEventItem(teamId, eventId, itemId, body): Observable<any[]> {
		return this.http
			.patch(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/item/' +
				itemId +
				'/profile',
				body
			)
			.pipe(map((res: any) => res.data));
	}

	deleteTeamEventItem(teamId, eventId, itemId): Observable<any[]> {
		return this.http
			.delete(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/item/' +
				itemId
			)
			.pipe(map((res: any) => res.data));
	}

	getAllTeamEventContactsExport(teamId, eventId): Observable<any[]> {
		const headers: HttpHeaders = new HttpHeaders({ 'X-Grip-Export': '1' });
		const qualifications: ContactStatus[] = [
			ContactStatus.CONTACT_STATUS_CONNECTED,
			ContactStatus.CONTACT_STATUS_SCANNED,
			ContactStatus.CONTACT_STATUS_MEETING
		];
		const params: HttpParams = buildUrlParams({ qualification: qualifications.join(','), format: 'csv' });

		return this.http
			.get(`${this.TEAMS_API_URL}team/${teamId}/event/${eventId}/contact`, { headers, params })
			.pipe(map((res: any) => res.data));
	}

	getAllTeamEventContacts(teamId, eventId): Observable<any[]> {
		const qualifications: ContactStatus[] = [
			ContactStatus.CONTACT_STATUS_CONNECTED,
			ContactStatus.CONTACT_STATUS_SCANNED,
			ContactStatus.CONTACT_STATUS_MEETING,
		];
		const params: HttpParams = buildUrlParams({ qualification: qualifications.join(',') });

		return this.http
			.get(`${this.TEAMS_API_URL}team/${teamId}/event/${eventId}/contact`, { params })
			.pipe(map((res: any) => res.data));
	}

	getPaginatedTeamEventContacts(teamId, eventId, page): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/contact' +
				'?qualification=connected,scanned,meeting_accepted' +
				'&page=' +
				page
			)
			.pipe(map((res: any) => res.data));
	}

	getTeamEventMemberContacts(teamId, eventId, userId): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/member/' +
				userId +
				'/contact' +
				'?qualification=connected,scanned,meeting_accepted'
			)
			.pipe(map((res: any) => res.data));
	}

	getLeadsSummary(teamId, eventId, userId) {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/member/' +
				userId +
				'/contact/inbound_lead/summary'
			)
			.pipe(map((res: any) => res.data));
	}

	getInboundLeads(teamId, eventId, userId, view = 'total', page = 0) {
		const param = page ? `?page=${page}` : '';
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/member/' +
				userId +
				'/contact/inbound_lead/' +
				view +
				param
			)
			.pipe(map((res: any) => res.data));
	}

	deleteContact(teamId, contactId): Observable<any[]> {
		return this.http
			.delete(this.TEAMS_API_URL + 'team/' + teamId + '/contact/' + contactId)
			.pipe(map((res: any) => res.data));
	}

	getInTeamStatus(thingId: number, eventScoped: boolean): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'team/thing/' +
				thingId +
				(eventScoped ? `?container_id=${this.configService.containerId}` : '')
			)
			.pipe(map((res: any) => res.data));
	}

	getTeamAvailability(teamId, eventId, param): Observable<{ schedule: any[] }> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/schedule/availability?' +
				param
			)
			.pipe(map((res: any) => res.data));
	}

	postTeamsSwipe(teamId, eventId, contactId): Observable<any[]> {
		return this.http
			.post(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/contact/' +
				contactId +
				'/connect',
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	postTeamEvent(body): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'team/event/create', body)
			.pipe(map((res: any) => res.data));
	}

	getBillingInfo(teamId): Observable<any> {
		return this.http
			.get(this.TEAMS_API_URL + 'billing/team/' + teamId)
			.pipe(map((res: any) => res.data));
	}

	getInvoices(teamId): Observable<any> {
		return this.http
			.get(this.TEAMS_API_URL + 'billing/team/' + teamId + '/invoice')
			.pipe(map((res: any) => res.data));
	}

	deleteConnection(userId, thingId): Observable<any> {
		return this.http
			.delete(this.API_URL + 'thing/' + userId + '/mutual/' + thingId)
			.pipe(map((res: any) => res.data));
	}

	// Premium packages
	getMaskedFields(teamId, containerId, thingId): Observable<any> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/container/' +
				containerId +
				'/thing/' +
				thingId +
				'/field/mask'
			)
			.pipe(map((res: any) => res.data));
	}

	// event premium packages
	getEventProducts(eventId): Observable<any[]> {
		return this.http
			.get(this.TEAMS_API_URL + 'billing/event/' + eventId + '/product')
			.pipe(map((res: any) => res.data));
	}

	getMonetizationItemLimits(teamId: number, eventId: number, companyTypeId: number): Observable<any[]> {
		return this.http
			.get(
				this.TEAMS_API_URL +
				`team/${teamId}/event/${eventId}/type/${companyTypeId}/item/create/limit`
			)
			.pipe(map((res: any) => res.data));
	}

	postTeamNameVerify(body): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'team/verify', body)
			.pipe(map((res: any) => res.data));
	}

	// hosted buyer
	getContainerMode(containerId, thingId): Observable<HostedBuyerContainerModeModel> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				containerId +
				'/thing/' +
				thingId +
				'/mode'
			)
			.pipe(map((res: any) => res.data));
	}

	// events availability
	getUserAvailability(userId: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'schedule/container/' +
				this.configService.containerId +
				'/thing/' +
				userId +
				'/availability/busy'
			)
			.pipe(map((res: any) => res.data));
	}

	updateUserAvailability(
		body: SubmitUserScheduleRequestModel,
		userId: number
	): Observable<string> {
		return this.http
			.put(
				this.API_URL +
				'schedule/container/' +
				this.configService.containerId +
				'/thing/' +
				userId +
				'/availability',
				body
			)
			.pipe(map((res: any) => res.data.message ?? res.data));
	}

	deleteUserAvailability(userId: number): Observable<string> {
		return this.http
			.delete(
				this.API_URL +
				'schedule/container/' +
				this.configService.containerId +
				'/thing/' +
				userId +
				'/availability'
			)
			.pipe(map((res: any) => res.data));
	}

	// teams Onboarding
	getTeamInvites(thingId): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'team/thing/' + thingId + '/invite')
			.pipe(map((res: any) => res.data));
	}

	getThingTeamJoinRequests(thingId): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'team/thing/' + thingId +
				'/join_request?container_id=' + this.configService.containerId)
			.pipe(map((res: any) => res.data));
	}

	getTeamJoinRequests(teamId): Observable<any[]> {
		return this.http
			.get(this.TEAMS_API_URL + 'team/' + teamId + '/join_request')
			.pipe(map((res: any) => res.data));
	}

	postAnswerTeamJoinRequest(teamId, requestId, answer): Observable<any[]> {
		return this.http
			.post(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/join_request/' +
				requestId +
				'/' +
				answer,
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	postAcceptTeamInvitation(body): Observable<any[]> {
		return this.http
			.post(this.TEAMS_API_URL + 'user/account/join', body)
			.pipe(map((res: any) => res.data));
	}

	postDeclineTeamInvitation(inviteRef): Observable<any[]> {
		return this.http
			.delete(
				this.TEAMS_API_URL + 'user/account/invite/link?linkref=' + inviteRef
			)
			.pipe(map((res: any) => res.data));
	}

	postRequestJoinTeam(teamId, thingId, body): Observable<any[]> {
		return this.http
			.post(
				this.TEAMS_API_URL +
				'user/team/' +
				teamId +
				'/thing/' +
				thingId +
				'/join_request',
				body
			)
			.pipe(map((res: any) => res.data));
	}

	getMatchingTeamsForThing(thingId): Observable<any[]> {
		return this.http
			.get(
				this.API_URL +
				'team/search/container/' +
				this.configService.containerId +
				'/thing/' +
				thingId
			)
			.pipe(map((res: any) => res.data));
	}

	// virtual meetings
	postVirtualMeeting(meetingId): Observable<any> {
		return this.http
			.post(this.API_URL + 'meeting/' + meetingId + '/virtual', {})
			.pipe(map((res: any) => res.data));
	}

	// notification banner
	getContainerNotice(): Observable<any[]> {
		return this.http
			.get(
				this.API_URL + 'container/' + this.configService.containerId + '/notice'
			)
			.pipe(map((res: any) => res.data));
	}

	// speed networking
	getSpeedNetworkingMeetings(sessionId, userId): Observable<any[]> {
		return this.http
			.get(
				this.API_URL + 'session/' + sessionId + '/thing/' + userId + '/meeting'
			)
			.pipe(map((res: any) => res.data));
	}

	getSessionAttendeeSearch(sessionId, params): Observable<any[]> {
		return this.http
			.get(
				this.API_URL +
				'session/' +
				sessionId +
				'/attendee/attendee/search' +
				params
			)
			.pipe(map((res: any) => res.data));
	}

	postSpeedNetworkingAttendeeArrived(
		sessionId: number,
		meetingId: number
	): Observable<any[]> {
		return this.http
			.post(
				this.API_URL +
				'session/' +
				sessionId +
				'/speed_meeting/' +
				meetingId +
				'/thing/arrived',
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	getUserNotificationSettings(): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'user/settings/notification')
			.pipe(map((res: any) => res.data));
	}

	putUserNotificationSettings(body): Observable<any[]> {
		return this.http
			.put(this.API_URL + 'user/settings/notification', body)
			.pipe(map((res: any) => res.data));
	}

	postUserEventSessionEnter(sessionId, source): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'session/' +
				sessionId +
				'/thing/user_id/self/click?source=' +
				source,
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	postUserEventSessionStart(sessionId, contentType, source): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'session/' +
				sessionId +
				`/thing/user_id/${contentType}/start?source=` +
				source,
				{}
			)
			.pipe(
				map(
					(res: any) => res.data
				),
				catchError(
					(error: HttpErrorResponse) => {
						console.log('error', error);
						return throwError(error);
					}
				)
			);
	}

	postUserEventSessionStop(sessionId, contentType, source): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'session/' +
				sessionId +
				`/thing/user_id/${contentType}/stop?source=` +
				source,
				{}
			)
			.pipe(
				map(
					(res: any) => res.data
				),
				catchError(
					(error: HttpErrorResponse) => {
						console.log('error', error);
						return throwError(error);
					}
				)
			);
	}

	postUserEventSessionWebsite(sessionId, source): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'session/' +
				sessionId +
				'/thing/user_id/website/click?source=' +
				source,
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	postUserEventSessionContent(sessionId, source): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'session/' +
				sessionId +
				'/thing/user_id/content/click?source=' +
				source,
				{}
			)
			.pipe(map((res: any) => res.data));
	}

	getHomeFeedForThing(thingId: number): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'userfeed/container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'?no_cache=' + Math.random()
			)
			.pipe(map((res: any) => res.data));
	}

	getPromotedSessionLocations(): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'session/location/container/' +
				this.configService.containerId +
				'/promoted'
			)
			.pipe(map((res: any) => res.data));
	}

	sendArenaChatMessage(sessionLocationId, body): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'chat/group/container/' +
				this.configService.containerId +
				'/sessloc/' +
				sessionLocationId,
				body
			)
			.pipe(map((res: any) => res.data));
	}

	sendSponsorMessage(thingId, body): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'chat/container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'/ask',
				body
			)
			.pipe(map((res: any) => res.data));
	}

	getUserEmail(): Observable<any> {
		return this.http
			.get(this.API_URL + 'user/email')
			.pipe(map((res: any) => res.data));
	}

	searchThingsForMeeting(params): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/search/meet' +
				params
			)
			.pipe(map((res: any) => res.data));
	}

	putMeetingInvitees(meetingId, body): Observable<any> {
		return this.http
			.put(this.API_URL + 'meeting/' + meetingId + '/multiuser/thing', body)
			.pipe(map((res: any) => res.data));
	}

	/**
	 * Teams: MustMeet meeting attendee reassignment / swap
	 */
	getMustMeetThingDetail(thingIds: number[]): Observable<any> {
		const params: HttpParams = buildUrlParams({ thing_ids: thingIds.join(',') });

		return this.http
			.get(this.API_URL + 'container/' + this.configService.containerId + '/mustmeet/thing/detail', { params })
			.pipe(map((res: any) => res.data));
	}

	patchMeetingSwap(meetings: MeetingSwapEntry[], dryRun: boolean = false): Observable<any> {
		return this.http
			.patch(this.API_URL + 'container/' + this.configService.containerId + '/meeting/swap', { meetings, dry_run: dryRun })
			.pipe(map((res: any) => res.data));
	}

	getMyScheduleDateFilters(thingId) {
		return this.http
			.get(
				this.API_URL +
				'schedule/' +
				'thing/' +
				thingId +
				'/day?container_id=' +
				this.configService.containerId
			)
			.pipe(map((res: any) => res.data));
	}

	getContactSharingVisibility(containerId) {
		return this.http
			.get(
				this.API_URL +
				'user/settings/container/' +
				containerId +
				'/contact_sharing'
			)
			.pipe(map((res: any) => res.data));
	}

	updateContactSharingVisibility(containerId, visibility): Observable<any> {
		return this.http
			.patch(
				this.API_URL +
				'user/settings/container/' +
				containerId +
				'/contact_sharing',
				{ contact_sharing: visibility }
			)
			.pipe(map((res: any) => res.data));
	}

	getContactCountSummary(teamId, eventId) {
		return this.http
			.get(
				this.TEAMS_API_URL +
				'team/' +
				teamId +
				'/event/' +
				eventId +
				'/contact/summary?qualification=connected,scanned,meeting_accepted'
			)
			.pipe(map((res: any) => res.data));
	}

	deleteThingImage(thingId, imageType): Observable<any> {
		return this.http
			.delete(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/' +
				thingId +
				'/image/' +
				imageType
			)
			.pipe(map((res: any) => res.data));
	}

	postDeviceTags(): Observable<any> {
		return this.http
			.post(
				this.API_URL +
				'thing/user_id/device-tags',
				{},
			)
			.pipe((res: any) => res);
	}

	getSessionCheckinExport(
		teamsEventId: number,
		teamId: number,
	): Observable<SessionCheckinExportModel[]> {
		const headers: HttpHeaders = new HttpHeaders({ 'X-Grip-Export': '1' });
		return this.http
			.get(`${this.TEAMS_API_URL}team/${teamId}/event/${teamsEventId}/session-in-person-scan`, { headers })
			.pipe(map((res: any) => res.data));
	}
	
	getTlrConfig(): Observable<TlrConfig> {
		return this.http
			.get(`${this.API_URL}container/${this.configService.containerId}/tiered_lead_retrieval`)
			.pipe(map((res: any) => res.data));
	}

	getFeatureControlSettings(): Observable<any> {
		return this.http
			.get(`${this.API_URL}container/${this.configService.containerId}/settings`)
			.pipe(map((res: any) => res.data));
	}
}
